import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Clock, AlertTriangle, CheckCircle, ChevronLeft, ChevronRight,
  Send, Loader2, Lock, Mail, User, Phone, Building, Key, Play, Flag,
  Timer, Eye, Shield, AlertCircle, Camera, CameraOff, Maximize,
  Volume2, VolumeX, Monitor, Upload, FileText, Download, ZoomIn, ZoomOut
} from 'lucide-react';
import Editor from '@monaco-editor/react';
import axios from 'axios';
import { toast } from 'sonner';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import PDFViewer from '../components/PDFViewer';

const API = process.env.REACT_APP_BACKEND_URL;

const MAX_VIOLATIONS = 10;
const VIOLATION_TYPES = {
  FULLSCREEN_EXIT: { type: 'fullscreen_exit', severity: 'high', message: 'You exited fullscreen mode' },
  TAB_SWITCH: { type: 'tab_switch', severity: 'high', message: 'You switched to another tab' },
  WINDOW_BLUR: { type: 'window_blur', severity: 'medium', message: 'Window lost focus' },
  COPY_ATTEMPT: { type: 'copy_attempt', severity: 'medium', message: 'Copy attempt detected' },
  PASTE_ATTEMPT: { type: 'paste_attempt', severity: 'medium', message: 'Paste attempt detected' },
  RIGHT_CLICK: { type: 'right_click', severity: 'low', message: 'Right-click attempt blocked' },
  SCREENSHOT_ATTEMPT: { type: 'screenshot_attempt', severity: 'high', message: 'Screenshot attempt detected' },
  DEV_TOOLS: { type: 'dev_tools', severity: 'critical', message: 'Developer tools detected' },
  FACE_NOT_DETECTED: { type: 'face_not_detected', severity: 'medium', message: 'Face not detected in camera' },
  MULTIPLE_FACES: { type: 'multiple_faces', severity: 'high', message: 'Multiple faces detected' }
};

// Render LaTeX content
const LaTeXRenderer = ({ content }) => {
  if (!content) return null;
  
  // Check if content has LaTeX delimiters
  const hasBlockMath = /\$\$[\s\S]+?\$\$/.test(content);
  const hasInlineMath = /\$[^$]+?\$/.test(content);
  
  if (!hasBlockMath && !hasInlineMath) {
    return <span>{content}</span>;
  }
  
  // Split content by LaTeX delimiters and render appropriately
  const parts = [];
  let remaining = content;
  let key = 0;
  
  // First handle block math ($$...$$)
  const blockRegex = /\$\$([\s\S]+?)\$\$/g;
  let lastIndex = 0;
  let match;
  
  while ((match = blockRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.slice(lastIndex, match.index), key: key++ });
    }
    parts.push({ type: 'block', content: match[1], key: key++ });
    lastIndex = match.index + match[0].length;
  }
  
  if (lastIndex < content.length) {
    parts.push({ type: 'text', content: content.slice(lastIndex), key: key++ });
  }
  
  // Now process inline math within text parts
  const finalParts = [];
  parts.forEach(part => {
    if (part.type === 'block') {
      finalParts.push(part);
    } else {
      const inlineRegex = /\$([^$]+?)\$/g;
      let textLastIndex = 0;
      let textMatch;
      const text = part.content;
      
      while ((textMatch = inlineRegex.exec(text)) !== null) {
        if (textMatch.index > textLastIndex) {
          finalParts.push({ type: 'text', content: text.slice(textLastIndex, textMatch.index), key: key++ });
        }
        finalParts.push({ type: 'inline', content: textMatch[1], key: key++ });
        textLastIndex = textMatch.index + textMatch[0].length;
      }
      
      if (textLastIndex < text.length) {
        finalParts.push({ type: 'text', content: text.slice(textLastIndex), key: key++ });
      }
    }
  });
  
  return (
    <span>
      {finalParts.map(part => {
        if (part.type === 'block') {
          try {
            return <BlockMath key={part.key} math={part.content} />;
          } catch (e) {
            return <span key={part.key} className="text-red-500">[Math Error]</span>;
          }
        } else if (part.type === 'inline') {
          try {
            return <InlineMath key={part.key} math={part.content} />;
          } catch (e) {
            return <span key={part.key} className="text-red-500">[Math Error]</span>;
          }
        } else {
          return <span key={part.key}>{part.content}</span>;
        }
      })}
    </span>
  );
};

// Webcam Proctoring Component
const WebcamProctoring = ({ enabled, onViolation, attemptId, challengeId }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  useEffect(() => {
    if (!enabled) return;
    
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: 'user', width: 320, height: 240 },
          audio: false 
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setCameraActive(true);
      } catch (err) {
        setCameraError('Camera access denied');
        console.error('Camera error:', err);
      }
    };
    
    startCamera();
    
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-gray-900 rounded-lg overflow-hidden shadow-xl">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-white text-xs">
          {cameraActive ? (
            <>
              <Camera size={12} className="text-green-400" />
              <span>Proctoring Active</span>
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            </>
          ) : (
            <>
              <CameraOff size={12} className="text-red-400" />
              <span>{cameraError || 'Initializing...'}</span>
            </>
          )}
        </div>
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="w-40 h-30 object-cover"
          style={{ transform: 'scaleX(-1)' }}
        />
      </div>
    </div>
  );
};

// Violation Warning Modal
const ViolationWarningModal = ({ isOpen, violation, violationCount, onReturnToFullscreen }) => {
  if (!isOpen) return null;
  const remaining = MAX_VIOLATIONS - violationCount;
  const isLastWarning = remaining <= 1;
  const isCritical = violation?.severity === 'critical' || violation?.severity === 'high';

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-pulse-once">
        <div className={`p-4 ${isLastWarning ? 'bg-red-600' : isCritical ? 'bg-orange-500' : 'bg-amber-500'}`}>
          <div className="flex items-center gap-3 text-white">
            <AlertTriangle size={28} />
            <div>
              <h2 className="text-lg font-bold">
                {isLastWarning ? 'FINAL WARNING!' : 'Proctoring Violation'}
              </h2>
              <p className="text-sm opacity-90">{violation?.message || 'Violation detected'}</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="text-center">
              <div className={`text-3xl font-bold ${isLastWarning ? 'text-red-600' : 'text-amber-600'}`}>
                {violationCount}
              </div>
              <div className="text-xs text-gray-500">Violations</div>
            </div>
            <div className="text-gray-300 text-2xl">/</div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-400">{MAX_VIOLATIONS}</div>
              <div className="text-xs text-gray-500">Maximum</div>
            </div>
          </div>
          
          {isLastWarning ? (
            <p className="text-red-600 text-center font-semibold mb-4">
              One more violation will auto-submit your assessment!
            </p>
          ) : (
            <p className="text-gray-600 text-center mb-4">
              {remaining} violations remaining before auto-submit
            </p>
          )}
          
          <button
            onClick={onReturnToFullscreen}
            className={`w-full py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 ${
              isLastWarning ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'
            }`}
          >
            <Maximize size={18} />
            Return to Fullscreen & Continue
          </button>
        </div>
      </div>
    </div>
  );
};

// Proctoring Status Bar
const ProctoringStatusBar = ({ violations, webcamEnabled, isFullscreen }) => {
  return (
    <div className="fixed top-0 left-0 right-0 bg-gray-900 text-white py-1 px-4 flex items-center justify-between text-xs z-50">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Monitor size={14} />
          <span className={isFullscreen ? 'text-green-400' : 'text-red-400'}>
            {isFullscreen ? 'Fullscreen' : 'Not Fullscreen'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Shield size={14} />
          <span>Proctoring Active</span>
        </div>
        {webcamEnabled && (
          <div className="flex items-center gap-2">
            <Camera size={14} className="text-green-400" />
            <span>Camera On</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className={violations.length > 5 ? 'text-red-400' : violations.length > 2 ? 'text-amber-400' : 'text-green-400'}>
          Violations: {violations.length}/{MAX_VIOLATIONS}
        </span>
      </div>
    </div>
  );
};

const EnterpriseChallengePage = () => {
  const { orgSlug, challengeSlug, challengeId: previewChallengeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const inviteToken = searchParams.get('invite');
  const previewToken = searchParams.get('token');
  const isPreviewMode = !!previewChallengeId && !!previewToken;
  
  // Stage: loading, access, test, submitted, error
  const [stage, setStage] = useState('loading');
  const [challenge, setChallenge] = useState(null);
  const [error, setError] = useState(null);
  
  // Access form
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [accessCode, setAccessCode] = useState(inviteToken || '');
  const [formError, setFormError] = useState('');
  const [submittingAccess, setSubmittingAccess] = useState(false);
  
  // Assessment state
  const [attemptData, setAttemptData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  
  // Navigation settings - controls whether candidates can navigate between questions
  const [allowNavigation, setAllowNavigation] = useState(true);
  const [lockAnswersOnProceed, setLockAnswersOnProceed] = useState(false);
  const [lockedQuestions, setLockedQuestions] = useState({});
  
  // Proctoring state
  const [violations, setViolations] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const [currentViolation, setCurrentViolation] = useState(null);
  const [isTestActive, setIsTestActive] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [webcamEnabled, setWebcamEnabled] = useState(false);
  const [proctoringSettings, setProctoringSettings] = useState({
    fullscreen_enforcement: true,
    tab_switch_detection: true,
    copy_paste_restriction: true,
    blur_detection: true,
    auto_warnings: true,
    auto_submit_on_violation: false,
    max_violations: 10,
    webcam_proctoring: false
  });
  
  // OTP Verification State (Advanced Mode)
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [verificationToken, setVerificationToken] = useState(null);
  const [activeRoundId, setActiveRoundId] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);
  
  // Advanced Submission State
  const [advancedMode, setAdvancedMode] = useState(false);
  const [submissionFiles, setSubmissionFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [activeRoundData, setActiveRoundData] = useState(null); // Store active round for PDF questions
  
  const timerRef = useRef(null);
  const violationDebounceRef = useRef(false);
  const devToolsCheckRef = useRef(null);

  // Fetch challenge on mount
  useEffect(() => {
    fetchChallenge();
  }, [orgSlug, challengeSlug, previewChallengeId, previewToken]);

  // Timer
  useEffect(() => {
    if (stage !== 'test' || !attemptData) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmit(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [stage, attemptData]);

  // Full Proctoring Setup
  useEffect(() => {
    if (!isTestActive) return;
    
    // Fullscreen change detection
    const handleFullscreenChange = () => {
      const isFs = !!document.fullscreenElement;
      setIsFullscreen(isFs);
      if (!isFs && isTestActive && proctoringSettings.fullscreen_enforcement) {
        handleViolation(VIOLATION_TYPES.FULLSCREEN_EXIT);
      }
    };
    
    // Tab visibility change
    const handleVisibilityChange = () => {
      if (document.hidden && proctoringSettings.tab_switch_detection) {
        handleViolation(VIOLATION_TYPES.TAB_SWITCH);
      }
    };
    
    // Window blur (losing focus)
    const handleWindowBlur = () => {
      if (proctoringSettings.blur_detection) {
        handleViolation(VIOLATION_TYPES.WINDOW_BLUR);
      }
    };
    
    // Copy prevention
    const handleCopy = (e) => {
      if (proctoringSettings.copy_paste_restriction) {
        e.preventDefault();
        handleViolation(VIOLATION_TYPES.COPY_ATTEMPT);
        return false;
      }
    };
    
    // Paste prevention
    const handlePaste = (e) => {
      if (proctoringSettings.copy_paste_restriction) {
        e.preventDefault();
        handleViolation(VIOLATION_TYPES.PASTE_ATTEMPT);
        return false;
      }
    };
    
    // Right-click prevention
    const handleContextMenu = (e) => {
      if (proctoringSettings.copy_paste_restriction) {
        e.preventDefault();
        handleViolation(VIOLATION_TYPES.RIGHT_CLICK);
        return false;
      }
    };
    
    // Screenshot prevention (PrintScreen key)
    const handleKeyDown = (e) => {
      // PrintScreen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        handleViolation(VIOLATION_TYPES.SCREENSHOT_ATTEMPT);
      }
      // Dev tools shortcuts
      if ((e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
          (e.ctrlKey && e.key === 'u') ||
          e.key === 'F12') {
        e.preventDefault();
        handleViolation(VIOLATION_TYPES.DEV_TOOLS);
      }
      // Prevent Ctrl+C, Ctrl+V, Ctrl+A
      if (proctoringSettings.copy_paste_restriction && e.ctrlKey && (e.key === 'c' || e.key === 'v' || e.key === 'a')) {
        e.preventDefault();
        handleViolation(e.key === 'c' ? VIOLATION_TYPES.COPY_ATTEMPT : VIOLATION_TYPES.PASTE_ATTEMPT);
      }
    };
    
    // Dev tools detection
    const checkDevTools = () => {
      const threshold = 160;
      const widthThreshold = window.outerWidth - window.innerWidth > threshold;
      const heightThreshold = window.outerHeight - window.innerHeight > threshold;
      if (widthThreshold || heightThreshold) {
        handleViolation(VIOLATION_TYPES.DEV_TOOLS);
      }
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    if (proctoringSettings.tab_switch_detection) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    if (proctoringSettings.blur_detection) {
      window.addEventListener('blur', handleWindowBlur);
    }
    if (proctoringSettings.copy_paste_restriction) {
      document.addEventListener('copy', handleCopy);
      document.addEventListener('paste', handlePaste);
      document.addEventListener('contextmenu', handleContextMenu);
    }
    document.addEventListener('keydown', handleKeyDown);
    
    // Check dev tools every 2 seconds
    devToolsCheckRef.current = setInterval(checkDevTools, 2000);
    
    // Disable text selection if copy/paste restricted
    if (proctoringSettings.copy_paste_restriction) {
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
    }
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      if (devToolsCheckRef.current) clearInterval(devToolsCheckRef.current);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    };
  }, [isTestActive]);

  const handleViolation = useCallback((violationType) => {
    if (violationDebounceRef.current) return;
    violationDebounceRef.current = true;
    
    const violation = {
      ...violationType,
      timestamp: new Date().toISOString()
    };
    
    setViolations(prev => {
      const newViolations = [...prev, violation];
      
      // Auto-submit if max violations reached and auto_submit is enabled
      const maxViolations = proctoringSettings.max_violations || MAX_VIOLATIONS;
      if (newViolations.length >= maxViolations && proctoringSettings.auto_submit_on_violation) {
        setTimeout(() => handleSubmit(true), 500);
      }
      
      return newViolations;
    });
    
    // Only show warning if auto_warnings is enabled
    if (proctoringSettings.auto_warnings) {
      setCurrentViolation(violation);
      setShowWarning(true);
    }
    
    // Log to backend
    if (attemptData && challenge) {
      axios.post(`${API}/api/enterprise/${challenge.id}/proctoring/log-violation`, null, {
        params: {
          attempt_id: attemptData.attempt_id,
          violation: JSON.stringify(violation)
        }
      }).catch(err => console.error('Failed to log violation:', err));
    }
    
    setTimeout(() => {
      violationDebounceRef.current = false;
    }, 1000);
  }, [attemptData, challenge]);

  const enterFullscreen = async () => {
    try {
      await document.documentElement.requestFullscreen();
      setIsFullscreen(true);
      setShowWarning(false);
    } catch (err) {
      toast.error('Please enable fullscreen mode to continue');
    }
  };

  const fetchChallenge = async () => {
    try {
      setStage('loading');
      let res;
      
      if (previewChallengeId && previewToken) {
        res = await axios.get(`${API}/api/enterprise/preview/${previewChallengeId}?token=${previewToken}`);
      } else if (orgSlug && challengeSlug) {
        res = await axios.get(`${API}/api/enterprise/${orgSlug}/${challengeSlug}`);
      } else {
        throw new Error('Invalid URL');
      }
      
      setChallenge(res.data.challenge);
      
      // Load proctoring settings from challenge or use defaults
      const challengeProctoring = res.data.challenge?.proctoring_settings || {};
      setProctoringSettings(prev => ({
        ...prev,
        ...challengeProctoring
      }));
      
      // Set webcam enabled based on settings
      setWebcamEnabled(
        challengeProctoring.webcam_proctoring || 
        res.data.challenge?.plan_details?.proctoring || 
        false
      );
      
      setStage('access');
    } catch (err) {
      let msg = 'Assessment not found';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          msg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          msg = detail.msg || detail.message || JSON.stringify(detail);
        } else {
          msg = String(detail);
        }
      }
      setError(msg);
      setStage('error');
    }
  };

  // OTP Functions for Advanced Mode 2FA
  const sendOtp = async (roundId) => {
    setOtpSending(true);
    setOtpError('');
    setOtp(''); // Clear any old OTP
    try {
      await axios.post(`${API}/api/enterprise/${challenge.id}/send-otp`, {
        email: email.trim().toLowerCase(),
        challenge_id: challenge.id,
        round_id: roundId
      });
      setActiveRoundId(roundId);
      setShowOtpModal(true);
      setResendCooldown(30); // 30 second cooldown before allowing resend
      toast.success('OTP sent to your email');
    } catch (err) {
      let msg = 'Failed to send OTP';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          msg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          msg = detail.msg || detail.message || JSON.stringify(detail);
        } else {
          msg = String(detail);
        }
      }
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }
    
    setOtpVerifying(true);
    setOtpError('');
    try {
      const res = await axios.post(`${API}/api/enterprise/${challenge.id}/verify-otp`, {
        email: email.trim().toLowerCase(),
        otp: otp,
        challenge_id: challenge.id,
        round_id: activeRoundId
      });
      
      setVerificationToken(res.data.verification_token);
      setShowOtpModal(false);
      setOtp('');
      setResendCooldown(0);
      toast.success('OTP verified! Starting assessment...');
      
      // Now proceed to start the assessment
      await startAssessmentAfterOtp(activeRoundId);
    } catch (err) {
      let errorDetail = 'Invalid OTP';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          errorDetail = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorDetail = detail.msg || detail.message || JSON.stringify(detail);
        } else {
          errorDetail = String(detail);
        }
      }
      let userMessage = errorDetail;
      if (errorDetail.includes('expired')) {
        userMessage = 'OTP has expired. Please click "Resend OTP" to get a new code.';
      } else if (errorDetail.includes('Invalid')) {
        userMessage = 'Invalid OTP. Please check the code and try again.';
      }
      setOtpError(userMessage);
      toast.error(userMessage);
    } finally {
      setOtpVerifying(false);
    }
  };

  const startAssessmentAfterOtp = async (roundId) => {
    try {
      const res = await axios.post(
        `${API}/api/enterprise/${challenge.id}/start-assessment/${roundId}?email=${encodeURIComponent(email.trim().toLowerCase())}`
      );
      
      setAttemptData(res.data);
      setQuestions(res.data.questions || []);
      
      // For resumed attempts, calculate remaining time from end_time; for new attempts use duration_minutes
      if (res.data.end_time) {
        const endTime = new Date(res.data.end_time);
        const now = new Date();
        const secondsLeft = Math.max(0, Math.floor((endTime - now) / 1000));
        setTimeLeft(secondsLeft);
      } else {
        setTimeLeft((res.data.duration_minutes || 60) * 60);
      }
      
      // Set navigation mode from round settings
      const navEnabled = res.data.allow_navigation === true || res.data.allow_navigation === undefined || res.data.allow_navigation === null;
      setAllowNavigation(navEnabled);
      setLockAnswersOnProceed(res.data.lock_answers_on_proceed || false);
      setLockedQuestions({});
      
      // Check if this is advanced mode round
      const activeRound = challenge?.rounds?.find(r => r.id === roundId);
      if (activeRound?.advanced_mode) {
        setAdvancedMode(true);
      }
      // Store active round data for PDF question paper display
      setActiveRoundData(activeRound);
      
      // Enter fullscreen and start
      await enterFullscreen();
      setIsTestActive(true);
      setStage('test');
    } catch (err) {
      let msg = 'Failed to start assessment';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          msg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          msg = detail.msg || detail.message || JSON.stringify(detail);
        } else {
          msg = String(detail);
        }
      }
      setFormError(msg);
      toast.error(msg);
    }
  };

  const handleStartTest = async (e) => {
    e.preventDefault();
    setFormError('');
    
    if (!email.trim()) {
      setFormError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError('Invalid email format');
      return;
    }
    
    const accessMode = challenge?.access_mode || 'public';
    if (accessMode === 'invite_only' && !inviteToken && !accessCode.trim()) {
      setFormError('Invite code is required');
      return;
    }
    
    setSubmittingAccess(true);
    
    try {
      // Register
      const regPayload = {
        name: name.trim() || email.split('@')[0],
        email: email.trim().toLowerCase(),
        phone: phone.trim() || '0000000000'
      };
      
      let regUrl = `${API}/api/enterprise/${challenge.id}/register`;
      const token = inviteToken || accessCode.trim();
      if (token) {
        regUrl += `?invite_token=${encodeURIComponent(token.toUpperCase())}`;
      }
      
      try {
        await axios.post(regUrl, regPayload);
      } catch (regErr) {
        if (!regErr.response?.data?.detail?.toLowerCase().includes('already registered')) {
          throw regErr;
        }
      }
      
      // Find active round
      const isRoundActive = (r) => {
        const status = (r.status || '').toLowerCase();
        return status === 'live' || status === 'active' || 
               (status && status !== 'draft' && status !== 'closed' && status !== 'archived');
      };
      
      const activeRound = challenge?.rounds?.find(isRoundActive);
      if (!activeRound) {
        if (!challenge?.rounds || challenge.rounds.length === 0) {
          setFormError('This assessment has not been configured yet.');
        } else {
          setFormError('Assessment is not currently active.');
        }
        setSubmittingAccess(false);
        return;
      }
      
      // Check if round requires 2FA (OTP verification)
      if (activeRound.security_2fa === 'email_otp' && !verificationToken) {
        // Need OTP verification first
        setSubmittingAccess(false);
        await sendOtp(activeRound.id);
        return;
      }
      
      // Check if this is advanced mode
      if (activeRound.advanced_mode) {
        setAdvancedMode(true);
      }
      
      // Start assessment
      const res = await axios.post(
        `${API}/api/enterprise/${challenge.id}/start-assessment/${activeRound.id}?email=${encodeURIComponent(email.trim().toLowerCase())}`
      );
      
      setAttemptData(res.data);
      setQuestions(res.data.questions || []);
      
      // For resumed attempts, calculate remaining time from end_time; for new attempts use duration_minutes
      if (res.data.end_time) {
        const endTime = new Date(res.data.end_time);
        const now = new Date();
        const secondsLeft = Math.max(0, Math.floor((endTime - now) / 1000));
        setTimeLeft(secondsLeft);
      } else {
        setTimeLeft((res.data.duration_minutes || 60) * 60);
      }
      
      // Set navigation mode from round settings
      const navEnabled = res.data.allow_navigation === true || res.data.allow_navigation === undefined || res.data.allow_navigation === null;
      setAllowNavigation(navEnabled);
      setLockAnswersOnProceed(res.data.lock_answers_on_proceed || false);
      setLockedQuestions({});
      
      // Enter fullscreen and start
      await enterFullscreen();
      setIsTestActive(true);
      setStage('test');
      
    } catch (err) {
      let msg = 'Failed to start assessment';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        // Handle Pydantic validation errors (array of objects) or string
        if (Array.isArray(detail)) {
          msg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          msg = detail.msg || detail.message || JSON.stringify(detail);
        } else {
          msg = String(detail);
        }
      }
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSubmittingAccess(false);
    }
  };

  const handleSubmit = async (isAuto = false) => {
    if (submitting) return;
    setSubmitting(true);
    
    try {
      // Check if we have PDF/Image files to upload (Advanced Mode or PDF question type)
      const hasPdfFiles = Object.keys(answers).some(key => key.endsWith('_pdf') && answers[key] instanceof File);
      const hasImageFiles = Object.keys(answers).some(key => key.endsWith('_images') && Array.isArray(answers[key]));
      const hasPdfQuestions = questions.some(q => q.type === 'pdf');
      
      // Use advanced submission if we have files OR if there are PDF questions
      if (advancedMode || hasPdfQuestions || hasPdfFiles || hasImageFiles) {
        // Advanced submission with file uploads
        const formData = new FormData();
        formData.append('submission_type', hasPdfFiles && hasImageFiles ? 'mixed' : hasPdfFiles ? 'pdf' : hasImageFiles ? 'image' : 'none');
        
        // Add PDF files
        Object.keys(answers).forEach(key => {
          if (key.endsWith('_pdf') && answers[key] instanceof File) {
            formData.append('pdf_files', answers[key], `${key.replace('_pdf', '')}.pdf`);
          }
        });
        
        // Add image files
        Object.keys(answers).forEach(key => {
          if (key.endsWith('_images') && Array.isArray(answers[key])) {
            answers[key].forEach((file, idx) => {
              formData.append('image_files', file, `${key.replace('_images', '')}_${idx}.${file.name.split('.').pop()}`);
            });
          }
        });
        
        // Add text answers (for any standard/subjective answers)
        const textAnswers = {};
        Object.keys(answers).forEach(key => {
          if (!key.endsWith('_pdf') && !key.endsWith('_pdf_name') && !key.endsWith('_images') && !key.endsWith('_images_count')) {
            textAnswers[key] = answers[key];
          }
        });
        formData.append('text_answers', JSON.stringify(textAnswers));
        formData.append('violations', JSON.stringify(violations));
        formData.append('time_taken', String((attemptData.duration_minutes * 60) - timeLeft));
        formData.append('auto_submitted', String(isAuto));
        
        const res = await axios.post(
          `${API}/api/enterprise/${challenge.id}/submit-advanced/${attemptData.attempt_id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        setResult(res.data);
      } else {
        // Standard submission
        const res = await axios.post(
          `${API}/api/enterprise/${challenge.id}/submit-assessment/${attemptData.attempt_id}`,
          { 
            answers, 
            violations,
            time_taken: (attemptData.duration_minutes * 60) - timeLeft,
            auto_submitted: isAuto
          }
        );
        
        setResult(res.data);
      }
      
      setStage('submitted');
      setIsTestActive(false);
      
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
      
      toast.success(isAuto ? 'Assessment auto-submitted due to violations' : 'Assessment submitted!');
    } catch (err) {
      let msg = 'Failed to submit';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          msg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          msg = detail.msg || detail.message || JSON.stringify(detail);
        } else {
          msg = String(detail);
        }
      }
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    }
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // Loading
  if (stage === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-600" size={48} />
      </div>
    );
  }

  // OTP Verification Modal - Must be checked before other stage-based returns
  if (showOtpModal) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Shield size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Verify Your Identity</h3>
                <p className="text-green-200 text-sm">Enter the OTP sent to your email</p>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6">
            <p className="text-gray-600 text-sm mb-4">
              We've sent a 6-digit verification code to <strong>{email}</strong>
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Enter OTP</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-3 text-center text-2xl tracking-widest font-mono border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all outline-none"
                placeholder="• • • • • •"
                maxLength={6}
                autoFocus
                data-testid="otp-input"
              />
            </div>
            
            {otpError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {otpError}
              </div>
            )}
            
            <button
              onClick={verifyOtp}
              disabled={otpVerifying || otp.length !== 6}
              className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
              data-testid="verify-otp-btn"
            >
              {otpVerifying ? (
                <><Loader2 className="animate-spin" size={18} /> Verifying...</>
              ) : (
                <><CheckCircle size={18} /> Verify & Start Test</>
              )}
            </button>
            
            <div className="mt-4 flex items-center justify-between text-sm">
              <button
                onClick={() => sendOtp(activeRoundId)}
                disabled={otpSending || resendCooldown > 0}
                className="text-green-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="resend-otp-btn"
              >
                {otpSending ? 'Sending...' : resendCooldown > 0 ? `Resend OTP (${resendCooldown}s)` : 'Resend OTP'}
              </button>
              <button
                onClick={() => { setShowOtpModal(false); setOtp(''); setOtpError(''); setResendCooldown(0); }}
                className="text-gray-500 hover:underline"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error
  if (stage === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Assessment Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button onClick={() => navigate('/')} className="px-6 py-2 bg-purple-600 text-white rounded-lg">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Preview Banner
  const PreviewBanner = () => isPreviewMode && (
    <div className="bg-amber-500 text-white py-2 px-4 text-center text-sm font-medium">
      <Eye size={16} className="inline mr-2" />
      PREVIEW MODE - This is how candidates see the assessment
    </div>
  );

  // Access Stage
  if (stage === 'access') {
    const accessMode = challenge?.access_mode || 'public';
    const isRoundActive = (r) => {
      const status = (r.status || '').toLowerCase();
      return status === 'live' || status === 'active' || 
             (status && status !== 'draft' && status !== 'closed' && status !== 'archived');
    };
    const activeRound = challenge?.rounds?.find(isRoundActive);
    
    return (
      <div className="min-h-screen bg-gray-100">
        <PreviewBanner />
        
        <div className="max-w-md mx-auto px-4 py-12">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-5 text-white">
              <h1 className="text-lg font-bold" data-testid="challenge-title">
                {challenge?.title || 'Assessment'}
              </h1>
              {activeRound && (
                <p className="text-purple-200 text-sm mt-1">
                  {activeRound.duration_minutes} mins • {activeRound.question_ids?.length || 0} questions
                </p>
              )}
              {accessMode !== 'public' && (
                <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 bg-white/20 rounded text-xs">
                  {accessMode === 'invite_only' ? <><Lock size={12} /> Invite Only</> : <><Shield size={12} /> Pre-Approved</>}
                </span>
              )}
              {!activeRound && (
                <p className="text-amber-200 text-sm mt-2">Assessment not yet active</p>
              )}
            </div>
            
            {/* Proctoring Notice */}
            <div className="bg-amber-50 border-b border-amber-200 p-3">
              <div className="flex items-center gap-2 text-amber-800 text-sm">
                <Shield size={16} />
                <span className="font-medium">Proctored Assessment</span>
              </div>
              <ul className="mt-2 text-xs text-amber-700 space-y-1 ml-6">
                <li>• Fullscreen mode required</li>
                <li>• Tab switching will be monitored</li>
                <li>• Copy/paste disabled</li>
                {webcamEnabled && <li>• Webcam proctoring enabled</li>}
              </ul>
            </div>
            
            <form onSubmit={handleStartTest} className="p-5 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {formError}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Enter your email"
                    data-testid="input-email"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Enter your name"
                    data-testid="input-name"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Enter your phone"
                    data-testid="input-phone"
                  />
                </div>
              </div>
              
              {accessMode === 'invite_only' && !inviteToken && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invite Code <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 uppercase"
                      placeholder="Enter invite code"
                      data-testid="input-access-code"
                    />
                  </div>
                </div>
              )}
              
              {accessMode === 'pre_approved' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                  <Shield size={14} className="inline mr-1" />
                  Only pre-approved email addresses can access this assessment.
                </div>
              )}
              
              <button
                type="submit"
                disabled={submittingAccess}
                className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="start-test-btn"
              >
                {submittingAccess ? (
                  <><Loader2 className="animate-spin" size={18} /> Starting...</>
                ) : (
                  <><Play size={18} /> Start Test</>
                )}
              </button>
              
              <p className="text-xs text-gray-500 text-center">
                By clicking Start Test, you agree to the proctoring guidelines
              </p>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Test Stage
  if (stage === 'test') {
    const q = questions[currentIndex];
    const answeredCount = Object.keys(answers).length;
    
    return (
      <>
        <ViolationWarningModal
          isOpen={showWarning && violations.length < MAX_VIOLATIONS}
          violation={currentViolation}
          violationCount={violations.length}
          onReturnToFullscreen={enterFullscreen}
        />
        
        <ProctoringStatusBar 
          violations={violations} 
          webcamEnabled={webcamEnabled}
          isFullscreen={isFullscreen}
        />
        
        <WebcamProctoring 
          enabled={webcamEnabled}
          onViolation={handleViolation}
          attemptId={attemptData?.attempt_id}
          challengeId={challenge?.id}
        />
        
        <div className="min-h-screen bg-gray-100 flex flex-col">
          {/* Minimal Fullscreen Header - Only Logo */}
          <div className="bg-white border-b shadow-sm py-2 px-4">
            <div className="max-w-6xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">C</span>
                  </div>
                  <span className="font-bold text-gray-900 text-sm">EvalentX</span>
                </div>
                <span className="text-gray-300">|</span>
                <span className="text-sm text-gray-600">{challenge?.title}</span>
              </div>
              
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">Q{currentIndex + 1} of {questions.length}</span>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-mono text-sm ${
                  timeLeft < 300 ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-gray-100 text-gray-700'
                }`}>
                  <Timer size={14} />
                  {formatTime(timeLeft)}
                </div>
                
                <button
                  onClick={() => handleSubmit(false)}
                  disabled={submitting}
                  className="px-4 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                  data-testid="submit-btn"
                >
                  {submitting ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                  Submit
                </button>
              </div>
            </div>
          </div>
          
          {/* PDF Question Paper Display (Advanced Mode) */}
          {activeRoundData?.question_paper_url && (
            <div className="p-4 pb-0">
              <div className="max-w-4xl mx-auto">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                        <FileText size={16} className="text-amber-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-amber-900">Question Paper</h4>
                        <p className="text-xs text-amber-700">Read all questions carefully before answering</p>
                      </div>
                    </div>
                    <a 
                      href={activeRoundData.question_paper_url.startsWith('http') ? activeRoundData.question_paper_url : `${API}${activeRoundData.question_paper_url}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium flex items-center gap-1 hover:bg-amber-600"
                    >
                      <Download size={12} /> Download PDF
                    </a>
                  </div>
                  <PDFViewer 
                    url={activeRoundData.question_paper_url.startsWith('http') ? activeRoundData.question_paper_url : `${API}${activeRoundData.question_paper_url}`} 
                    fileName="Question_Paper.pdf"
                    embedded={true}
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Question */}
          <div className="flex-1 p-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="bg-white/20 px-2 py-1 rounded text-sm">Q{currentIndex + 1}</span>
                    <span className="text-sm text-purple-200">{q?.points || 10} pts</span>
                  </div>
                  <button
                    onClick={() => setFlagged(prev => ({ ...prev, [q.id]: !prev[q.id] }))}
                    className={`p-1.5 rounded ${flagged[q?.id] ? 'bg-amber-400' : 'bg-white/20'}`}
                  >
                    <Flag size={14} />
                  </button>
                </div>
                
                <div className="p-6">
                  {/* Question Type Badge */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      q?.type === 'mcq' ? 'bg-cyan-100 text-cyan-700' : 
                      q?.type === 'subjective' ? 'bg-amber-100 text-amber-700' :
                      q?.type === 'pdf' ? 'bg-red-100 text-red-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {q?.type?.toUpperCase() || 'MCQ'}
                    </span>
                    <span className="text-xs text-gray-500">{q?.points || 10} points</span>
                    {q?.difficulty && (
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        q.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                        q.difficulty === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {q.difficulty}
                      </span>
                    )}
                  </div>
                  
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    <LaTeXRenderer content={q?.title || q?.problem_statement || q?.content || 'Question'} />
                  </h3>
                  
                  {/* Question Image */}
                  {q?.image_url && (
                    <div className="mb-6">
                      <img 
                        src={`${API}/api/company/enterprise/files/${q.image_url}`}
                        alt="Question illustration"
                        className="max-w-full max-h-64 rounded-lg border mx-auto"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  
                  {/* MCQ Options */}
                  {q?.type === 'mcq' && q?.options && (
                    <div className="space-y-3">
                      {q.options.map((opt, idx) => (
                        <label
                          key={idx}
                          className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                            answers[q.id] === opt
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                          data-testid={`option-${idx}`}
                        >
                          <input
                            type="radio"
                            name={`q-${q.id}`}
                            checked={answers[q.id] === opt}
                            onChange={() => setAnswers(prev => ({ ...prev, [q.id]: opt }))}
                            className="sr-only"
                          />
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                            answers[q.id] === opt ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
                          }`}>
                            {answers[q.id] === opt && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                          <div className="flex-1">
                            <span className="text-gray-700">
                              <LaTeXRenderer content={opt} />
                            </span>
                            {/* Option Image */}
                            {q.option_images?.[idx] && (
                              <div className="mt-2">
                                <img 
                                  src={`${API}/api/company/enterprise/files/${q.option_images[idx]}`}
                                  alt={`Option ${idx + 1}`}
                                  className="max-h-32 rounded border"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  {/* Subjective / Descriptive Questions */}
                  {q?.type === 'subjective' && (
                    <div className="space-y-4">
                      {q.word_limit && (
                        <p className="text-sm text-gray-500">
                          Maximum {q.word_limit} words allowed
                        </p>
                      )}
                      <textarea
                        value={answers[q.id] || ''}
                        onChange={(e) => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                        className="w-full h-64 px-4 py-3 border rounded-lg text-gray-700 resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Write your answer here..."
                        data-testid="subjective-answer"
                      />
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>
                          Words: {(answers[q.id] || '').split(/\s+/).filter(w => w).length}
                          {q.word_limit && ` / ${q.word_limit}`}
                        </span>
                        <span>Characters: {(answers[q.id] || '').length}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Coding Problems - Competitive Programming Format */}
                  {q?.type === 'coding' && (
                    <div className="space-y-4">
                      {/* Problem Details */}
                      {(q.input_format || q.output_format || q.constraints) && (
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3 text-sm">
                          {q.input_format && (
                            <div>
                              <h4 className="font-semibold text-gray-700 mb-1">Input Format</h4>
                              <pre className="text-gray-600 whitespace-pre-wrap font-mono text-xs bg-white p-2 rounded border">
                                {q.input_format}
                              </pre>
                            </div>
                          )}
                          {q.output_format && (
                            <div>
                              <h4 className="font-semibold text-gray-700 mb-1">Output Format</h4>
                              <pre className="text-gray-600 whitespace-pre-wrap font-mono text-xs bg-white p-2 rounded border">
                                {q.output_format}
                              </pre>
                            </div>
                          )}
                          {q.constraints && (
                            <div>
                              <h4 className="font-semibold text-gray-700 mb-1">Constraints</h4>
                              <pre className="text-gray-600 whitespace-pre-wrap font-mono text-xs bg-white p-2 rounded border">
                                {q.constraints}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Sample I/O */}
                      {(q.sample_input || q.sample_output) && (
                        <div className="grid grid-cols-2 gap-4">
                          {q.sample_input && (
                            <div>
                              <h4 className="font-semibold text-gray-700 mb-1 text-sm">Sample Input</h4>
                              <pre className="bg-gray-900 text-green-400 p-3 rounded-lg font-mono text-xs overflow-auto max-h-32">
                                {q.sample_input}
                              </pre>
                            </div>
                          )}
                          {q.sample_output && (
                            <div>
                              <h4 className="font-semibold text-gray-700 mb-1 text-sm">Sample Output</h4>
                              <pre className="bg-gray-900 text-green-400 p-3 rounded-lg font-mono text-xs overflow-auto max-h-32">
                                {q.sample_output}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Code Editor */}
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-gray-800 px-4 py-2 flex items-center justify-between">
                          <span className="text-sm text-gray-300">Code Editor</span>
                          <select
                            className="bg-gray-700 text-gray-200 text-sm px-2 py-1 rounded"
                            defaultValue="python"
                          >
                            <option value="python">Python</option>
                            <option value="javascript">JavaScript</option>
                            <option value="cpp">C++</option>
                            <option value="java">Java</option>
                          </select>
                        </div>
                        <Editor
                          height="350px"
                          defaultLanguage="python"
                          value={answers[q.id] || '# Write your code here\n\ndef solve():\n    # Your solution\n    pass\n\nif __name__ == "__main__":\n    solve()\n'}
                          onChange={(val) => setAnswers(prev => ({ ...prev, [q.id]: val }))}
                          options={{ minimap: { enabled: false }, fontSize: 14 }}
                          theme="vs-dark"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Advanced Mode - File Upload for PDF/Image submissions */}
                  {advancedMode && q?.type === 'subjective' && (
                    <div className="mt-6 border-t pt-6">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                          <Upload size={16} className="text-amber-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Upload Answer Sheet</h4>
                          <p className="text-xs text-gray-500">Upload handwritten or scanned answers (PDF/Images)</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {/* PDF Upload */}
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-purple-400 transition-colors">
                          <input
                            type="file"
                            accept=".pdf"
                            id={`pdf-upload-${q.id}`}
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setAnswers(prev => ({
                                  ...prev,
                                  [`${q.id}_pdf`]: file,
                                  [`${q.id}_pdf_name`]: file.name
                                }));
                              }
                            }}
                          />
                          <label 
                            htmlFor={`pdf-upload-${q.id}`}
                            className="cursor-pointer flex flex-col items-center text-center"
                          >
                            <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mb-2">
                              <FileText size={24} className="text-red-500" />
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                              {answers[`${q.id}_pdf_name`] || 'Upload PDF'}
                            </span>
                            <span className="text-xs text-gray-400 mt-1">Max 10MB</span>
                          </label>
                        </div>
                        
                        {/* Image Upload */}
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-purple-400 transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            id={`image-upload-${q.id}`}
                            className="hidden"
                            onChange={(e) => {
                              const files = Array.from(e.target.files);
                              if (files.length > 0) {
                                setAnswers(prev => ({
                                  ...prev,
                                  [`${q.id}_images`]: files,
                                  [`${q.id}_images_count`]: files.length
                                }));
                              }
                            }}
                          />
                          <label 
                            htmlFor={`image-upload-${q.id}`}
                            className="cursor-pointer flex flex-col items-center text-center"
                          >
                            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-2">
                              <Camera size={24} className="text-blue-500" />
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                              {answers[`${q.id}_images_count`] ? `${answers[`${q.id}_images_count`]} image(s)` : 'Upload Images'}
                            </span>
                            <span className="text-xs text-gray-400 mt-1">JPG, PNG</span>
                          </label>
                        </div>
                      </div>
                      
                      {(answers[`${q.id}_pdf_name`] || answers[`${q.id}_images_count`]) && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg flex items-center gap-2">
                          <CheckCircle size={16} className="text-green-600" />
                          <span className="text-sm text-green-700">Files ready for upload on submit</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* PDF Question Type - View PDF and Upload Answer */}
                  {q?.type === 'pdf' && (
                    <div className="space-y-6">
                      {/* Instructions */}
                      {q.title && (
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                          <p className="text-sm text-amber-800">
                            <strong>Instructions:</strong> {q.title}
                          </p>
                        </div>
                      )}
                      
                      {/* PDF Question Paper Display */}
                      {q.pdf_url && (
                        <div className="border-2 border-red-200 rounded-xl overflow-hidden">
                          <div className="p-4 bg-red-50 border-b border-red-200 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                <FileText size={20} className="text-red-600" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-red-900">Question Paper</h4>
                                <p className="text-xs text-red-700">{q.pdf_file_name || 'View the PDF below'}</p>
                              </div>
                            </div>
                            <a 
                              href={q.pdf_url.startsWith('http') ? q.pdf_url : `${API}${q.pdf_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-red-600"
                            >
                              <Download size={14} /> Download PDF
                            </a>
                          </div>
                          <div className="bg-gray-100">
                            <PDFViewer 
                              url={q.pdf_url.startsWith('http') ? q.pdf_url : `${API}${q.pdf_url}`} 
                              fileName={q.pdf_file_name || 'Question_Paper.pdf'}
                              embedded={true}
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Answer Upload Section */}
                      <div className="border-2 border-purple-200 rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Upload size={20} className="text-purple-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">Submit Your Answer</h4>
                            <p className="text-xs text-gray-500">Upload your answer sheet as PDF or images</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          {/* PDF Answer Upload */}
                          {q.allow_pdf_answer !== false && (
                            <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-red-400 transition-colors">
                              <input
                                type="file"
                                accept=".pdf"
                                id={`pdf-answer-${q.id}`}
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    setAnswers(prev => ({
                                      ...prev,
                                      [`${q.id}_pdf`]: file,
                                      [`${q.id}_pdf_name`]: file.name
                                    }));
                                  }
                                }}
                              />
                              <label 
                                htmlFor={`pdf-answer-${q.id}`}
                                className="cursor-pointer flex flex-col items-center text-center"
                              >
                                <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-3">
                                  <FileText size={28} className="text-red-500" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                  {answers[`${q.id}_pdf_name`] || 'Upload PDF Answer'}
                                </span>
                                <span className="text-xs text-gray-400 mt-1">Single PDF file, max 10MB</span>
                              </label>
                            </div>
                          )}
                          
                          {/* Image Answer Upload */}
                          {q.allow_image_answer !== false && (
                            <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-blue-400 transition-colors">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                id={`image-answer-${q.id}`}
                                className="hidden"
                                onChange={(e) => {
                                  const files = Array.from(e.target.files);
                                  if (files.length > 0) {
                                    setAnswers(prev => ({
                                      ...prev,
                                      [`${q.id}_images`]: files,
                                      [`${q.id}_images_count`]: files.length
                                    }));
                                  }
                                }}
                              />
                              <label 
                                htmlFor={`image-answer-${q.id}`}
                                className="cursor-pointer flex flex-col items-center text-center"
                              >
                                <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-3">
                                  <Camera size={28} className="text-blue-500" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                  {answers[`${q.id}_images_count`] ? `${answers[`${q.id}_images_count`]} image(s) selected` : 'Upload Image(s)'}
                                </span>
                                <span className="text-xs text-gray-400 mt-1">Multiple images allowed</span>
                              </label>
                            </div>
                          )}
                        </div>
                        
                        {/* Upload Status */}
                        {(answers[`${q.id}_pdf_name`] || answers[`${q.id}_images_count`]) && (
                          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                            <CheckCircle size={18} className="text-green-600" />
                            <span className="text-sm text-green-700 font-medium">
                              {answers[`${q.id}_pdf_name`] && `PDF: ${answers[`${q.id}_pdf_name`]}`}
                              {answers[`${q.id}_pdf_name`] && answers[`${q.id}_images_count`] && ' + '}
                              {answers[`${q.id}_images_count`] && `${answers[`${q.id}_images_count`]} image(s)`}
                              {' '}- Ready for submission
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Navigation */}
                <div className="border-t p-4 flex items-center justify-between bg-gray-50">
                  {allowNavigation ? (
                    <button
                      onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
                      disabled={currentIndex === 0}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 flex items-center gap-2"
                      data-testid="prev-btn"
                    >
                      <ChevronLeft size={16} /> Previous
                    </button>
                  ) : (
                    <div className="px-4 py-2 border border-gray-200 rounded-lg text-gray-400 flex items-center gap-2 cursor-not-allowed">
                      <ChevronLeft size={16} /> Previous
                    </div>
                  )}
                  
                  <span className="text-sm text-gray-600">{answeredCount}/{questions.length} answered</span>
                  
                  <button
                    onClick={() => {
                      if (!allowNavigation && lockAnswersOnProceed) {
                        // Lock current question when moving forward in sequential mode
                        const currentQId = questions[currentIndex]?.id;
                        if (currentQId) {
                          setLockedQuestions(prev => ({ ...prev, [currentQId]: true }));
                        }
                      }
                      setCurrentIndex(prev => Math.min(questions.length - 1, prev + 1));
                    }}
                    disabled={currentIndex === questions.length - 1}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                    data-testid="next-btn"
                  >
                    Next <ChevronRight size={16} />
                    {lockAnswersOnProceed && !allowNavigation && <Lock size={12} />}
                  </button>
                </div>
              </div>
              
              {/* Question Navigator */}
              <div className="mt-4 bg-white rounded-lg shadow p-4">
                {!allowNavigation && (
                  <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700 flex items-center gap-1.5">
                      <AlertTriangle size={12} />
                      Sequential mode: You can only proceed forward
                    </p>
                  </div>
                )}
                <p className="text-sm font-medium text-gray-700 mb-3">Questions</p>
                <div className="flex flex-wrap gap-2">
                  {questions.map((qn, idx) => {
                    const isCurrentQuestion = idx === currentIndex;
                    const isNextQuestion = idx === currentIndex + 1;
                    const isPreviousQuestion = idx < currentIndex;
                    const isFutureQuestion = idx > currentIndex + 1;
                    const isLocked = lockedQuestions[qn.id];
                    const canClick = allowNavigation || isCurrentQuestion || isNextQuestion;
                    
                    return (
                      <button
                        key={qn.id}
                        onClick={() => {
                          if (allowNavigation) {
                            setCurrentIndex(idx);
                          } else if (isNextQuestion) {
                            // In sequential mode, can only go to next question
                            if (lockAnswersOnProceed) {
                              const currentQId = questions[currentIndex]?.id;
                              if (currentQId) {
                                setLockedQuestions(prev => ({ ...prev, [currentQId]: true }));
                              }
                            }
                            setCurrentIndex(idx);
                          }
                          // Cannot go back or jump ahead in sequential mode
                        }}
                        disabled={!allowNavigation && !canClick}
                        className={`w-8 h-8 rounded text-sm font-medium relative ${
                          isCurrentQuestion
                            ? 'bg-purple-600 text-white'
                            : isLocked
                            ? 'bg-orange-100 text-orange-600 border border-orange-300 cursor-not-allowed'
                            : !allowNavigation && isPreviousQuestion
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : !allowNavigation && isFutureQuestion
                            ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                            : answers[qn.id]
                            ? 'bg-green-100 text-green-700 border border-green-300'
                            : flagged[qn.id]
                            ? 'bg-amber-100 text-amber-700 border border-amber-300'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {idx + 1}
                        {isLocked && <Lock size={8} className="absolute -top-1 -right-1 text-orange-500" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Submitted
  if (stage === 'submitted') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Assessment Submitted!</h1>
          <p className="text-gray-600 mb-6">Your responses have been recorded.</p>
          
          {result && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Score</span>
                <span className="font-semibold">{result.score || 0}/{result.total_points || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Percentage</span>
                <span className="font-semibold">{result.percentage || 0}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status</span>
                <span className={`font-semibold ${result.passed ? 'text-green-600' : 'text-gray-600'}`}>
                  {result.passed ? 'Passed' : 'Completed'}
                </span>
              </div>
              {violations.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Violations</span>
                  <span className="font-semibold text-amber-600">{violations.length}</span>
                </div>
              )}
            </div>
          )}
          
          <button onClick={() => navigate('/')} className="px-6 py-2 bg-purple-600 text-white rounded-lg">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default EnterpriseChallengePage;
